// =============================================================================
// HERO SECTION — Above-the-Fold Component
// =============================================================================
// The first thing visitors see. A full-viewport section with a background
// image (or video), the restaurant name, tagline, and 1-2 CTA buttons.
//
// VARIANTS (controlled by style pack tokens):
// - "fullbleed": Full-screen image/video with text overlay (most common)
// - "split": Half image, half text (Squarespace-style)
// - "carousel": Rotating 3-5 images (auto-advancing)
//
// PERFORMANCE:
// - Hero image is the Largest Contentful Paint (LCP) element
// - Uses fetchPriority="high" and loading="eager" to load it ASAP
// - Video falls back to a static poster image on mobile to save bandwidth
// =============================================================================

import type { ReactNode } from 'react';
import './Hero.css';

export interface HeroProps {
  /** Hero headline (usually the restaurant name) */
  headline: string;
  /** Tagline shown below the headline */
  tagline?: string;
  /** Background image URL — CRITICAL for performance (LCP element) */
  backgroundImage: string;
  /** Alt text for the background image (screen reader accessibility) */
  backgroundAlt?: string;
  /** Optional background video URL (desktop only, muted autoplay) */
  backgroundVideo?: string;
  /** Primary CTA button (e.g., "Reserve a Table") */
  primaryCTA?: ReactNode;
  /** Secondary CTA button (e.g., "View Menu") */
  secondaryCTA?: ReactNode;
  /** Hero visual variant */
  variant?: 'fullbleed' | 'split';
  /** If true, relies on global AtmosphereBackground without duplicate bg image */
  transparentBackground?: boolean;
  /** Section HTML id */
  id?: string;
  /** Additional CSS class */
  className?: string;
}

/**
 * Full-viewport hero section.
 *
 * USAGE:
 *   <Hero
 *     headline="Trattoria da Marco"
 *     tagline="Farm-to-table Italian cuisine since 1985"
 *     backgroundImage="/images/hero.webp"
 *     primaryCTA={<CTAButton variant="primary" as="link" href="/reservations">Reserve</CTAButton>}
 *     secondaryCTA={<CTAButton variant="secondary" as="link" href="/menu">Our Menu</CTAButton>}
 *   />
 */
export function Hero({
  headline,
  tagline,
  backgroundImage,
  backgroundAlt = 'Restaurant ambiance',
  backgroundVideo,
  primaryCTA,
  secondaryCTA,
  variant = 'fullbleed',
  transparentBackground = false,
  id = 'hero',
  className = '',
}: HeroProps) {
  if (variant === 'split') {
    return (
      <section id={id} className={`hero hero-split ${className}`} aria-label="Welcome">
        {/* --- Image Half --- */}
        <div className="hero-split-image">
          <img
            src={backgroundImage}
            alt={backgroundAlt}
            className="img-cover"
            loading="eager"
            /* fetchPriority tells the browser this is the MOST IMPORTANT
               image on the page — load it before anything else */
            fetchPriority="high"
          />
        </div>

        {/* --- Text Half --- */}
        <div className="hero-split-content">
          <h1 className="hero-headline">{headline}</h1>
          {tagline && <p className="hero-tagline">{tagline}</p>}
          {(primaryCTA || secondaryCTA) && (
            <div className="hero-actions">
              {primaryCTA}
              {secondaryCTA}
            </div>
          )}
        </div>
      </section>
    );
  }

  // Default: fullbleed variant
  return (
    <section id={id} className={`hero hero-fullbleed ${className}`} aria-label="Welcome">
      {/* --- Background Image (rendered only if not transparentBackground) --- */}
      {!transparentBackground && (
        <div className="hero-background" aria-hidden="true">
          <img
            src={backgroundImage}
            alt={backgroundAlt}
            className="img-cover hero-bg-image"
            loading="eager"
            fetchPriority="high"
          />

          {/* Video overlay (desktop only, muted autoplay) */}
          {backgroundVideo && (
            <video
              className="hero-bg-video hidden-mobile"
              autoPlay
              muted
              loop
              playsInline
              poster={backgroundImage}
              aria-hidden="true"
            >
              <source src={backgroundVideo} type="video/mp4" />
            </video>
          )}

          {/* Dark gradient overlay for text legibility */}
          <div className="hero-overlay" />
        </div>
      )}

      {/* --- Content --- */}
      <div className="hero-content container">
        <h1 className="hero-headline">{headline}</h1>
        {tagline && <p className="hero-tagline">{tagline}</p>}
        {(primaryCTA || secondaryCTA) && (
          <div className="hero-actions">
            {primaryCTA}
            {secondaryCTA}
          </div>
        )}
      </div>
    </section>
  );
}
